import type { ThemeConfig } from "antd";
import { ConfigProvider, Pagination, theme as antdTheme } from "antd";
import { useMemo, type ReactNode } from "react";
import {
  analyzeThemeColor,
  DEFAULT_PRIMARY_COLOR,
  resolveSecondaryColor,
  SECONDARY_FOREGROUND,
} from "./themeColor";

const createProductTheme = (primaryColor: string, secondaryColor?: string | null): ThemeConfig => {
  const secondary = resolveSecondaryColor(secondaryColor);
  const analysis = analyzeThemeColor(primaryColor);
  const resolvedPrimary = analysis.color || DEFAULT_PRIMARY_COLOR;
  const primarySoft = `color-mix(in srgb, ${resolvedPrimary} 10%, white)`;
  return {
    token: {
      colorPrimary: secondary.color,
      colorInfo: resolvedPrimary,
      colorLink: resolvedPrimary,
      colorTextLightSolid: SECONDARY_FOREGROUND,
      colorBorder: "#E5E7EB",
      colorText: "#131B2A",
      colorTextSecondary: "#667085",
      borderRadius: 8,
      controlHeight: 42,
      fontFamily: "Noto Sans, sans-serif",
      boxShadow: "0 1px 2px rgba(19, 27, 42, 0.05)",
      boxShadowSecondary: "0 12px 32px rgba(19, 27, 42, 0.10)",
    },
    components: {
      Button: {
        colorPrimary: secondary.color,
        colorPrimaryHover: secondary.hover,
        colorPrimaryActive: secondary.active,
        primaryColor: SECONDARY_FOREGROUND,
        primaryShadow: "none",
        defaultShadow: "none",
        fontWeight: 600,
      },
      Card: {
        headerBg: "#FFFFFF",
        headerFontSize: 15,
        bodyPadding: 20,
      },
      // Keep popup cells compact when the product theme uses 42px input controls.
      DatePicker: {
        cellWidth: 36,
        cellHeight: 24,
        textHeight: 32,
        withoutTimeCellHeight: 54,
        timeColumnWidth: 56,
        timeColumnHeight: 168,
      },
      Form: {
        itemMarginBottom: 18,
        labelColor: "#131B2A",
        labelFontSize: 13,
      },
      Input: {
        activeBorderColor: resolvedPrimary,
        activeShadow: `0 0 0 2px color-mix(in srgb, ${resolvedPrimary} 16%, transparent)`,
        hoverBorderColor: resolvedPrimary,
      },
      Modal: {
        titleColor: "#131B2A",
        titleFontSize: 18,
        contentBg: "#FFFFFF",
        headerBg: "#FFFFFF",
        footerBg: "#FFFFFF",
      },
      Checkbox: {
        colorPrimary: resolvedPrimary,
        colorPrimaryHover: resolvedPrimary,
      },
      Pagination: {
        itemActiveBg: primarySoft,
        itemBg: "#FFFFFF",
      },
      Select: {
        activeBorderColor: resolvedPrimary,
        activeOutlineColor: `color-mix(in srgb, ${resolvedPrimary} 16%, transparent)`,
        hoverBorderColor: resolvedPrimary,
        optionSelectedBg: primarySoft,
        optionSelectedColor: "#131B2A",
      },
      Switch: {
        colorPrimary: resolvedPrimary,
        colorPrimaryHover: resolvedPrimary,
      },
      Table: {
        borderColor: "#E5E7EB",
        headerBg: "#F7F8FA",
        headerColor: "#667085",
        rowHoverBg: primarySoft,
        rowSelectedBg: primarySoft,
        rowSelectedHoverBg: `color-mix(in srgb, ${resolvedPrimary} 16%, white)`,
        cellPaddingBlock: 11,
        cellPaddingInline: 14,
      },
      Tabs: {
        inkBarColor: resolvedPrimary,
        itemActiveColor: "#131B2A",
        itemHoverColor: "#131B2A",
        itemSelectedColor: "#131B2A",
      },
    },
  };
};

type ProductPageShellProps = {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  density?: "default" | "compact";
  primaryColor?: string | null;
  secondaryColor?: string | null;
};

type ProductPageHeaderProps = {
  eyebrow?: string;
  title: string;
  description: string;
  // Keep product-page headers typographic by default; reserve icons for meaningful status or workflow context, not decoration.
  icon?: ReactNode;
  actions?: ReactNode;
};

type ProductSectionCardProps = {
  children: ReactNode;
  title: string;
  description?: string;
  eyebrow?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
};

type ProductStatusBadgeProps = {
  children: ReactNode;
  tone?: "gold" | "neutral" | "success" | "danger";
};

type ProductPaginationProps = {
  current: number;
  pageSize: number;
  total: number;
  itemLabel: string;
  onChange: (page: number, pageSize: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  compact?: boolean;
  pageSizeOptions?: string[];
};

const statusToneClasses: Record<NonNullable<ProductStatusBadgeProps["tone"]>, string> = {
  gold: "border-(--brand-primary)/40 bg-(--brand-primary)/10 text-(--brand-primary)",
  neutral: "border-gray-200 bg-gray-50 text-[#131B2A]",
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  danger: "border-red-200 bg-red-50 text-red-700",
};

/**
 * Applies the TripboxCRM authenticated-product Ant Design theme to a subtree.
 */
export const ProductPageProvider = ({ children, primaryColor, secondaryColor }: ProductPageShellProps) => {
  const { token } = antdTheme.useToken();
  const productTheme = useMemo(
    () => createProductTheme(primaryColor || token.colorInfo || DEFAULT_PRIMARY_COLOR, secondaryColor),
    [primaryColor, secondaryColor, token.colorInfo],
  );
  return <ConfigProvider theme={productTheme}>{children}</ConfigProvider>;
};

/**
 * Provides the standard background, spacing, and width constraints for authenticated product pages.
 */
export const ProductPageShell = ({
  children,
  className = "",
  contentClassName = "",
  density = "default",
}: ProductPageShellProps) => (
  <main className={`min-h-[calc(100vh-5rem)] border-t border-gray-200 bg-[#FBFBFB] ${className}`}>
    <div
      className={`mx-auto w-full max-w-7xl px-3 sm:px-4 lg:px-6 ${
        density === "compact" ? "pb-4 pt-3 sm:pt-4" : "pb-16 pt-8 sm:px-6 lg:px-8 lg:pt-10"
      } ${contentClassName}`}
    >
      {children}
    </div>
  </main>
);

/**
 * Renders a high-contrast page heading aligned to the authenticated product design system.
 */
export const ProductPageHeader = ({ eyebrow, title, description, icon, actions }: ProductPageHeaderProps) => (
  <header className="mb-8 flex flex-col gap-6 border-b border-gray-200 pb-7 lg:flex-row lg:items-end lg:justify-between">
    <div className="flex min-w-0 items-start gap-4">
      {icon ? (
        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-(--brand-primary) shadow-[0_1px_2px_rgba(19,27,42,0.05)]">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-(--brand-primary)">{eyebrow}</p>
        ) : null}
        <h1 className="text-balance text-3xl font-semibold leading-tight tracking-tight text-[#131B2A] sm:text-4xl">
          {title}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">{description}</p>
      </div>
    </div>
    {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
  </header>
);

/**
 * Renders a reusable structural card for forms, settings, and operational content.
 */
export const ProductSectionCard = ({
  children,
  title,
  description,
  eyebrow,
  icon,
  action,
  className = "",
}: ProductSectionCardProps) => (
  <section
    className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.05)] ${className}`}
  >
    <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
      <div className="flex min-w-0 items-start gap-3">
        {icon ? (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-(--brand-primary)/30 bg-(--brand-primary)/10 text-(--brand-primary)">
            {icon}
          </div>
        ) : null}
        <div className="min-w-0">
          {eyebrow ? (
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-(--brand-primary)">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="text-base font-semibold tracking-tight text-[#131B2A] sm:text-lg">{title}</h2>
          {description ? <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">{description}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

/**
 * Displays a compact branded status for account and workflow states.
 */
export const ProductStatusBadge = ({ children, tone = "neutral" }: ProductStatusBadgeProps) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${statusToneClasses[tone]}`}
  >
    {children}
  </span>
);

/**
 * Renders the standard list footer used across authenticated product tables.
 */
export const ProductPagination = ({
  current,
  pageSize,
  total,
  itemLabel,
  onChange,
  onPageSizeChange,
  compact = false,
  pageSizeOptions = ["10", "20", "50", "100"],
}: ProductPaginationProps) => {
  if (total <= 0) return null;

  const rangeStart = (current - 1) * pageSize + 1;
  const rangeEnd = Math.min(current * pageSize, total);

  return (
    <footer
      aria-label={`${itemLabel} pagination`}
      className={`flex border-t border-gray-200 bg-white px-3 py-3 ${
        compact ? "flex-col items-center gap-2" : "flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      }`}
    >
      <p className="text-xs font-medium tabular-nums text-gray-500">
        {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()} {itemLabel}
      </p>
      <Pagination
        current={current}
        pageSize={pageSize}
        total={total}
        size="small"
        responsive
        showLessItems
        showQuickJumper={!compact}
        showSizeChanger={!compact && Boolean(onPageSizeChange)}
        pageSizeOptions={pageSizeOptions}
        onChange={(nextPage, nextPageSize) => {
          if (nextPageSize !== pageSize && onPageSizeChange) return;
          onChange(nextPage, nextPageSize);
        }}
        onShowSizeChange={(_, nextPageSize) => {
          onPageSizeChange?.(nextPageSize);
        }}
        className="!m-0 shrink-0 [&_.ant-pagination-item-active]:!border-(--brand-primary) [&_.ant-pagination-item-active]:!bg-[var(--brand-primary-soft)] [&_.ant-pagination-item-active_a]:!font-semibold [&_.ant-pagination-item-active_a]:!text-(--brand-primary)"
      />
    </footer>
  );
};

/** Keeps combined date/time panels usable within narrow, short viewports. */
export const PRODUCT_DATE_TIME_POPUP_CLASS_NAME = [
  "max-sm:!fixed max-sm:!left-3 max-sm:!top-3",
  "max-sm:!max-h-[calc(100dvh-1.5rem)] max-sm:!overflow-y-auto",
  "max-sm:[&_.ant-picker-datetime-panel]:!flex-col",
  "max-sm:[&_.ant-picker-panel-container]:!max-w-[calc(100vw-1.5rem)]",
].join(" ");
